import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Repeat,
  AlertCircle,
  CheckSquare,
  Calendar,
  RefreshCw,
  ShoppingCart,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Card } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { statsApi } from '../api/stats';
import { receiptsApi } from '../api/receipts';
import { recurringExpensesApi, type RecurringExpense } from '../api/recurringExpenses';
import { todosApi } from '../api/todos';
import { budgetsApi, type BudgetStatusResponse } from '../api/budgets';
import { householdsApi } from '../api/households';
import type { TodoSummary } from '../api/todos';
import type { SummaryResponse, CategorySpendingItem, StoreSavingsItem } from '../api/stats';
import type { RecurringPurchase, Household } from '../types';
import type { Receipt as ReceiptType } from '../types';
import { formatEUR } from '../utils/format';
import { useAuthStore } from '../store/authStore';
import { toast } from '../components/ui/Toast';

const CHART_COLORS = [
  '#3B82F6', '#8B5CF6', '#F59E0B', '#22C55E', '#EF4444',
  '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
];

// =============================================
// Period Filter Tabs
// =============================================
type Period = 'all' | 'month' | 'week';

interface PeriodTab {
  key: Period;
  label: string;
}

const PERIOD_TABS: PeriodTab[] = [
  { key: 'week', label: 'Woche' },
  { key: 'month', label: 'Monat' },
  { key: 'all', label: 'Gesamt' },
];

const PeriodFilterTabs: React.FC<{
  active: Period;
  onChange: (p: Period) => void;
}> = ({ active, onChange }) => (
  <div className="inline-flex items-center gap-1 p-1 rounded-2xl bg-white/60 dark:bg-[#1e1e2e]/60 backdrop-blur-sm border border-white/40 dark:border-indigo-500/10 shadow-sm">
    {PERIOD_TABS.map((tab) => (
      <button
        key={tab.key}
        onClick={() => onChange(tab.key)}
        className={`px-4 py-1.5 rounded-xl text-sm font-semibold transition-all duration-200
          ${
            active === tab.key
              ? 'bg-gradient-to-r from-indigo-500 to-violet-500 text-white shadow-md'
              : 'text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 hover:bg-white/60 dark:hover:bg-white/5'
          }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);

// =============================================
// Category Donut Chart
// =============================================
const CATEGORY_EMOJIS: Record<string, string> = {
  'obst & gemüse': '\uD83E\uDD6C',
  'fleisch & fisch': '\uD83E\uDD69',
  'milchprodukte': '\uD83E\uDDC0',
  'backwaren': '\uD83C\uDF5E',
  'getränke': '\uD83E\uDD64',
  'süßigkeiten & snacks': '\uD83C\uDF6B',
  'tiefkühl': '\u2744\uFE0F',
  'konserven & fertig': '\uD83E\uDD6B',
  'gewürze & öle': '\uD83E\uDED2',
  'haushalt': '\uD83E\uDDF9',
  'drogerie & kosmetik': '\uD83E\uDDF4',
  'sonstiges': '\uD83D\uDCE6',
};

const CategoryDonutCard: React.FC<{ data: CategorySpendingItem[] }> = ({ data }) => {
  const total = data.reduce((sum, d) => sum + d.total, 0);

  return (
    <Card className="animate-slide-up animate-fill-both animation-delay-400" padding="lg">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-neutral-800 dark:text-white flex items-center gap-2">
          {'\uD83C\uDFAF'} Top-Kategorien
        </h2>
        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">Wo geht dein Geld hin?</p>
      </div>

      {data.length > 0 ? (
        <div className="flex flex-row items-center gap-4">
          {/* Donut — hidden on very small screens, compact on mobile */}
          <div className="hidden sm:block w-28 h-28 md:w-36 md:h-36 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data.slice(0, 6)}
                  cx="50%"
                  cy="50%"
                  innerRadius="55%"
                  outerRadius="85%"
                  paddingAngle={3}
                  dataKey="total"
                  stroke="none"
                >
                  {data.slice(0, 6).map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="flex-1 space-y-1.5 w-full">
            {data.slice(0, 6).map((cat, index) => {
              const pct = total > 0 ? Math.round((cat.total / total) * 100) : 0;
              return (
                <div key={cat.category_name} className="flex items-center gap-1.5">
                  <div
                    className="w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                  />
                  <span className="text-xs shrink-0">
                    {CATEGORY_EMOJIS[cat.category_name.toLowerCase()] || '\uD83D\uDCE6'}
                  </span>
                  <span className="text-xs sm:text-sm text-neutral-700 dark:text-neutral-300 truncate flex-1">
                    {cat.category_name}
                  </span>
                  <span className="text-xs sm:text-sm font-mono font-bold tabular-nums text-neutral-800 dark:text-neutral-100 shrink-0">
                    {formatEUR(cat.total)}
                  </span>
                  <span className="text-[10px] sm:text-xs text-neutral-400 shrink-0 w-8 sm:w-10 text-right">
                    {pct}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 text-neutral-400 gap-2">
          <span className="text-4xl">{'\uD83C\uDFAF'}</span>
          <p className="text-sm">Noch keine Kategorien erfasst</p>
        </div>
      )}
    </Card>
  );
};

// =============================================
// Stat Card (Gradient)
// =============================================
interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  emoji: string;
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  gradient: string;
  delay: number;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  emoji,
  trend,
  trendValue,
  gradient,
  delay,
  onClick,
}) => (
  <div
    onClick={onClick}
    className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg
                hover:-translate-y-1 hover:shadow-xl transition-all duration-300
                ${onClick ? 'cursor-pointer' : 'cursor-default'}
                animate-slide-up animate-fill-both ${gradient}`}
    style={{ animationDelay: `${delay}ms` }}
  >
    {/* Decorative circle */}
    <div className="absolute -top-4 -right-4 w-24 h-24 bg-white/10 rounded-full" />
    <div className="absolute -bottom-6 -right-6 w-20 h-20 bg-white/5 rounded-full" />

    <div className="relative z-10">
      <div className="flex items-center justify-between mb-3">
        <span className="text-2xl">{emoji}</span>
        {trend && trendValue && (
          <span className="inline-flex items-center gap-0.5 text-xs font-medium bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">
            {trend === 'up' ? (
              <ArrowUpRight className="w-3 h-3" />
            ) : (
              <ArrowDownRight className="w-3 h-3" />
            )}
            {trendValue}
          </span>
        )}
      </div>
      <p className="text-sm font-medium text-white/80">{title}</p>
      <p className="text-2xl font-bold font-mono tabular-nums mt-1 tracking-tight">
        {value}
      </p>
      {subtitle && (
        <p className="text-xs text-white/60 mt-1">{subtitle}</p>
      )}
    </div>
  </div>
);

// =============================================
// Receipt Timeline Item
// =============================================
const ReceiptTimelineItem: React.FC<{
  receipt: ReceiptType;
  onClick: () => void;
  index: number;
}> = ({ receipt, onClick, index }) => (
  <div
    onClick={onClick}
    className="flex items-center gap-4 px-4 py-3 rounded-xl hover:bg-white/60 dark:hover:bg-white/5
               cursor-pointer transition-all duration-200 group
               animate-slide-up animate-fill-both"
    style={{ animationDelay: `${300 + index * 80}ms` }}
  >
    {/* Timeline dot */}
    <div className="relative flex flex-col items-center">
      <div className="w-3 h-3 rounded-full bg-gradient-to-br from-primary-400 to-secondary-500 shadow-sm
                      group-hover:scale-125 transition-transform duration-200" />
      {index < 4 && (
        <div className="w-0.5 h-8 bg-gradient-to-b from-primary-200 to-transparent mt-1" />
      )}
    </div>

    <div className="flex-1 min-w-0">
      <p className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 truncate group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
        {receipt.store?.name || receipt.store_name || 'Unbekannter Laden'}
      </p>
      <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
        {receipt.receipt_date
          ? new Date(receipt.receipt_date).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })
          : 'Kein Datum'}
      </p>
    </div>
    <div className="text-right shrink-0">
      <p className="text-sm font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100">
        {receipt.total_amount != null
          ? formatEUR(receipt.total_amount)
          : '--,-- \u20AC'}
      </p>
    </div>
    <ChevronRight className="w-4 h-4 text-neutral-300 shrink-0 group-hover:text-primary-400 group-hover:translate-x-0.5 transition-all" />
  </div>
);

// =============================================
// Savings by Store Chart
// =============================================
const SavingsByStoreCard: React.FC<{ data: StoreSavingsItem[] }> = ({ data }) => {
  const maxSavings = Math.max(...data.map((d) => d.savings), 1);

  return (
    <Card className="animate-slide-up animate-fill-both animation-delay-500" padding="lg">
      <div className="mb-5">
        <h2 className="text-lg font-bold text-neutral-800 dark:text-white flex items-center gap-2">
          {'\uD83C\uDFF7\uFE0F'} Ersparnis nach Laden
        </h2>
        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">Rabatte & Gutscheine pro Laden</p>
      </div>

      {data.length > 0 ? (
        <div className="space-y-3">
          {data.map((item, index) => {
            const percentage = (item.savings / maxSavings) * 100;
            return (
              <div key={item.store_name} className="group">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 truncate">
                    {item.store_name}
                  </span>
                  <span className="text-sm font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100 ml-2 shrink-0">
                    {formatEUR(item.savings)}
                  </span>
                </div>
                <div className="w-full h-2.5 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                    }}
                  />
                </div>
                <p className="text-xs text-neutral-400 mt-0.5">
                  {item.receipt_count} {item.receipt_count === 1 ? 'Beleg' : 'Belege'}
                </p>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 text-neutral-400 gap-2">
          <span className="text-4xl">{'\uD83C\uDFF7\uFE0F'}</span>
          <p className="text-sm">Noch keine Rabatte erfasst</p>
        </div>
      )}
    </Card>
  );
};

// =============================================
// Fixkosten Card (Dashboard Widget)
// =============================================
function toMonthlyAmountDash(amount: number, interval: RecurringExpense['interval']): number {
  switch (interval) {
    case 'weekly':   return amount * 52 / 12;
    case 'monthly':  return amount;
    case 'quarterly': return amount / 3;
    case 'yearly':   return amount / 12;
  }
}

const FixkostenCard: React.FC<{ expenses: RecurringExpense[]; onNavigate: () => void }> = ({
  expenses,
  onNavigate,
}) => {
  const active = expenses.filter((e) => e.is_active);
  const monthlyTotal = active.reduce(
    (sum, e) => sum + toMonthlyAmountDash(Number(e.amount) || 0, e.interval),
    0,
  );

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const nextDue = active
    .filter((e) => new Date(e.next_due_date) >= today)
    .sort((a, b) => new Date(a.next_due_date).getTime() - new Date(b.next_due_date).getTime())[0];

  const dueSoonCount = active.filter((e) => {
    const d = new Date(e.next_due_date);
    d.setHours(0, 0, 0, 0);
    const diff = (d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 7;
  }).length;

  return (
    <Card
      hover
      onClick={onNavigate}
      className="animate-slide-up animate-fill-both animation-delay-600"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
            <Repeat className="w-4 h-4 text-indigo-500" aria-hidden="true" />
          </div>
          <h2 className="text-base font-bold text-neutral-800 dark:text-white">Fixkosten</h2>
        </div>
        {dueSoonCount > 0 && (
          <span className="inline-flex items-center gap-1 text-xs font-semibold
                           bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300
                           px-2 py-0.5 rounded-full"
                aria-label={`${dueSoonCount} bald faellig`}
          >
            <AlertCircle className="w-3 h-3" />
            {dueSoonCount} faellig
          </span>
        )}
      </div>

      <p className="text-2xl font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100 tracking-tight">
        {formatEUR(monthlyTotal)}
        <span className="text-sm font-normal text-neutral-400 dark:text-neutral-500 ml-1">/Monat</span>
      </p>

      {nextDue && (
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1.5 flex items-center gap-1">
          <span>Naechste Zahlung:</span>
          <span className="font-medium text-neutral-600 dark:text-neutral-300">
            {nextDue.icon || '\uD83D\uDCE6'} {nextDue.name}
          </span>
          <span>&mdash;</span>
          <span>
            {new Date(nextDue.next_due_date).toLocaleDateString('de-DE', {
              day: '2-digit',
              month: 'short',
            })}
          </span>
        </p>
      )}

      {active.length === 0 && (
        <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1.5">
          Noch keine Fixkosten erfasst
        </p>
      )}

      <div className="flex items-center gap-1 mt-3 text-xs font-semibold text-primary-500 hover:text-primary-600">
        Alle anzeigen
        <ChevronRight className="w-3.5 h-3.5" />
      </div>
    </Card>
  );
};

// =============================================
// Monatsübersicht Donut Card
// =============================================
const MONAT_COLORS = {
  fixkosten: '#6366F1',   // Indigo
  variabel:  '#F97316',   // Orange
};

interface MonatsuebersichtCardProps {
  fixkostenMonatlich: number;
  variableAusgaben: number;
}

const MonatsuebersichtCard: React.FC<MonatsuebersichtCardProps> = ({
  fixkostenMonatlich,
  variableAusgaben,
}) => {
  const gesamt = fixkostenMonatlich + variableAusgaben;
  const hasData = gesamt > 0;

  const donutData = hasData
    ? [
        { name: 'Fixkosten',          value: fixkostenMonatlich },
        { name: 'Variable Ausgaben',  value: variableAusgaben   },
      ]
    : [];

  const fixPct  = gesamt > 0 ? Math.round((fixkostenMonatlich / gesamt) * 100) : 0;
  const varPct  = gesamt > 0 ? Math.round((variableAusgaben  / gesamt) * 100) : 0;

  return (
    <Card className="animate-slide-up animate-fill-both animation-delay-650" padding="lg">
      {/* Header */}
      <div className="mb-5">
        <h2 className="text-lg font-bold text-neutral-800 dark:text-white flex items-center gap-2">
          {'\uD83D\uDCC6'} Monatsübersicht
        </h2>
        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-0.5">
          Fixkosten vs. variable Ausgaben
        </p>
      </div>

      {hasData ? (
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Donut Chart */}
          <div className="relative w-44 h-44 shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  cx="50%"
                  cy="50%"
                  innerRadius="58%"
                  outerRadius="85%"
                  paddingAngle={3}
                  dataKey="value"
                  stroke="none"
                  startAngle={90}
                  endAngle={-270}
                >
                  <Cell fill={MONAT_COLORS.fixkosten} />
                  <Cell fill={MONAT_COLORS.variabel}  />
                </Pie>
                <Tooltip
                  formatter={(value) => [formatEUR(Number(value)), '']}
                  contentStyle={{
                    borderRadius: '0.75rem',
                    border: 'none',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
                    fontSize: '0.75rem',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Gesamtbetrag in der Mitte */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="text-[10px] text-neutral-400 dark:text-neutral-500 font-medium uppercase tracking-wide leading-none">
                Gesamt
              </span>
              <span className="text-base font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100 mt-0.5 leading-tight">
                {formatEUR(gesamt)}
              </span>
            </div>
          </div>

          {/* Legende */}
          <div className="flex-1 space-y-4 w-full">
            {/* Fixkosten */}
            <div className="flex items-start gap-3">
              <div
                className="mt-0.5 w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: MONAT_COLORS.fixkosten }}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                    Fixkosten
                  </span>
                  <span className="text-sm font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100 shrink-0">
                    {formatEUR(fixkostenMonatlich)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-neutral-400 dark:text-neutral-500">
                    Miete, Handy, Abos …
                  </span>
                  <span className="text-xs font-medium text-neutral-400 dark:text-neutral-500 shrink-0">
                    {fixPct}%
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-1.5 h-1.5 w-full bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${fixPct}%`, backgroundColor: MONAT_COLORS.fixkosten }}
                  />
                </div>
              </div>
            </div>

            {/* Variable Ausgaben */}
            <div className="flex items-start gap-3">
              <div
                className="mt-0.5 w-3 h-3 rounded-full shrink-0"
                style={{ backgroundColor: MONAT_COLORS.variabel }}
                aria-hidden="true"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">
                    Variable Ausgaben
                  </span>
                  <span className="text-sm font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100 shrink-0">
                    {formatEUR(variableAusgaben)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-0.5">
                  <span className="text-xs text-neutral-400 dark:text-neutral-500">
                    Einkäufe & Kassenzettel
                  </span>
                  <span className="text-xs font-medium text-neutral-400 dark:text-neutral-500 shrink-0">
                    {varPct}%
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-1.5 h-1.5 w-full bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${varPct}%`, backgroundColor: MONAT_COLORS.variabel }}
                  />
                </div>
              </div>
            </div>

            {/* Trennlinie + Gesamt-Zeile */}
            <div className="pt-3 border-t border-neutral-100 dark:border-neutral-800">
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-neutral-700 dark:text-neutral-200">
                  Gesamt
                </span>
                <span className="text-base font-bold font-mono tabular-nums text-neutral-900 dark:text-white">
                  {formatEUR(gesamt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 text-neutral-400 gap-2">
          <span className="text-4xl" aria-hidden="true">{'\uD83D\uDCC6'}</span>
          <p className="text-sm">Noch keine Daten vorhanden</p>
          <p className="text-xs text-neutral-300 dark:text-neutral-600">
            Erfasse Belege und Fixkosten um die Monatsübersicht zu sehen
          </p>
        </div>
      )}

      <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-3">
        Fixkosten = monatliche Abos &amp; Verträge
      </p>
    </Card>
  );
};

// =============================================
// Aufgaben Card (Dashboard Widget)
// =============================================
const AufgabenCard: React.FC<{ summary: TodoSummary; onNavigate: () => void }> = ({
  summary,
  onNavigate,
}) => {
  const totalOpen = summary.personal_open + summary.household_open;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const dueDateLabel = (dateStr: string): string => {
    const due = new Date(dateStr);
    due.setHours(0, 0, 0, 0);
    const diff = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return `${Math.abs(diff)}T ueberfaellig`;
    if (diff === 0) return 'Heute';
    return due.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  };

  const dueDateColor = (dateStr: string): string => {
    const due = new Date(dateStr);
    due.setHours(0, 0, 0, 0);
    const diff = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diff < 0) return 'text-red-500 dark:text-red-400';
    if (diff === 0) return 'text-amber-500 dark:text-amber-400';
    return 'text-neutral-400 dark:text-neutral-500';
  };

  return (
    <Card
      hover
      onClick={onNavigate}
      className="animate-slide-up animate-fill-both animation-delay-600"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center">
            <CheckSquare className="w-4 h-4 text-primary-500" aria-hidden="true" />
          </div>
          <h2 className="text-base font-bold text-neutral-800 dark:text-white">Aufgaben</h2>
        </div>
        {totalOpen > 0 && (
          <span
            className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1 rounded-full
                       bg-primary-500 text-white text-[11px] font-bold shadow-sm"
            aria-label={`${totalOpen} offene Aufgaben`}
          >
            {totalOpen > 99 ? '99+' : totalOpen}
          </span>
        )}
      </div>

      {totalOpen > 0 ? (
        <>
          <p className="text-2xl font-bold font-mono tabular-nums text-neutral-800 dark:text-neutral-100 tracking-tight">
            {totalOpen}
            <span className="text-sm font-normal text-neutral-400 dark:text-neutral-500 ml-1">
              offen
            </span>
          </p>

          {(summary.personal_open > 0 || summary.household_open > 0) && (
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
              {summary.personal_open > 0 && (
                <span>{summary.personal_open} persoenlich</span>
              )}
              {summary.personal_open > 0 && summary.household_open > 0 && (
                <span> &middot; </span>
              )}
              {summary.household_open > 0 && (
                <span>{summary.household_open} Haushalt</span>
              )}
            </p>
          )}

          {(summary.upcoming ?? []).length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {summary.upcoming.slice(0, 3).map((item) => (
                <li key={item.id} className="flex items-center gap-2 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 shrink-0" aria-hidden="true" />
                  <span className="flex-1 truncate text-neutral-600 dark:text-neutral-300 font-medium">
                    {item.title}
                  </span>
                  {item.due_date && (
                    <span className={`shrink-0 flex items-center gap-0.5 ${dueDateColor(item.due_date)}`}>
                      <Calendar className="w-3 h-3" aria-hidden="true" />
                      {dueDateLabel(item.due_date)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <p className="text-sm text-neutral-400 dark:text-neutral-500 mt-1">
          Alle Aufgaben erledigt
        </p>
      )}

      <div className="flex items-center gap-1 mt-3 text-xs font-semibold text-primary-500 hover:text-primary-600">
        Alle anzeigen
        <ChevronRight className="w-3.5 h-3.5" />
      </div>
    </Card>
  );
};

// =============================================
// Wiederkehrende Einkäufe Card (Dashboard Widget)
// =============================================
const WiederkehrendeEinkäufeCard: React.FC<{ items: RecurringPurchase[] }> = ({ items }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isBaldFällig = (nextExpected: string): boolean => {
    const date = new Date(nextExpected);
    date.setHours(0, 0, 0, 0);
    const diffDays = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 3;
  };

  const formatNextExpected = (dateStr: string): string => {
    const date = new Date(dateStr);
    date.setHours(0, 0, 0, 0);
    const diffDays = Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return `vor ${Math.abs(diffDays)} Tagen`;
    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Morgen';
    if (diffDays <= 3) return `in ${diffDays} Tagen`;
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  };

  const handleAddToShoppingList = (e: React.MouseEvent, itemName: string) => {
    e.stopPropagation();
    toast.info(`"${itemName}" zur Einkaufsliste hinzufügen — Kommt bald`);
  };

  return (
    <Card className="animate-slide-up animate-fill-both animation-delay-700" padding="lg">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center">
            <RefreshCw className="w-4 h-4 text-emerald-500" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-base font-bold text-neutral-800 dark:text-white">
              Regelmäßige Einkäufe
            </h2>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
              Wiederkehrende Artikel
            </p>
          </div>
        </div>
        {items.some((i) => isBaldFällig(i.next_expected)) && (
          <span
            className="inline-flex items-center gap-1 text-xs font-semibold
                       bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300
                       px-2 py-0.5 rounded-full"
            aria-label="Artikel bald fällig"
          >
            <AlertCircle className="w-3 h-3" />
            Bald fällig
          </span>
        )}
      </div>

      {items.length > 0 ? (
        <ul className="space-y-2">
          {items.slice(0, 5).map((item) => {
            const baldFällig = isBaldFällig(item.next_expected);
            return (
              <li
                key={item.name}
                className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors
                  ${baldFällig
                    ? 'bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20'
                    : 'bg-neutral-50 dark:bg-white/5'
                  }`}
              >
                {/* Linke Seite: Name + Meta */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p
                      className={`text-sm font-semibold truncate
                        ${baldFällig
                          ? 'text-emerald-700 dark:text-emerald-300'
                          : 'text-neutral-800 dark:text-neutral-100'
                        }`}
                    >
                      {item.name}
                    </p>
                    {baldFällig && (
                      <span
                        className="shrink-0 inline-flex items-center text-[10px] font-bold
                                   bg-emerald-500 text-white px-1.5 py-0.5 rounded-full"
                        aria-label="Bald fällig"
                      >
                        Bald
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                      Ø {Number(item.avg_price).toLocaleString('de-DE', {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                    </span>
                    <span className="text-neutral-300 dark:text-neutral-600 text-xs select-none">·</span>
                    <span className="text-xs text-neutral-400 dark:text-neutral-500">
                      Alle {Math.round(item.avg_days_between)} Tage
                    </span>
                    <span className="text-neutral-300 dark:text-neutral-600 text-xs select-none">·</span>
                    <span
                      className={`text-xs flex items-center gap-0.5
                        ${baldFällig
                          ? 'text-emerald-600 dark:text-emerald-400 font-semibold'
                          : 'text-neutral-400 dark:text-neutral-500'
                        }`}
                    >
                      <Calendar className="w-3 h-3 shrink-0" aria-hidden="true" />
                      {formatNextExpected(item.next_expected)}
                    </span>
                  </div>
                </div>

                {/* Rechte Seite: Zur Einkaufsliste */}
                <button
                  onClick={(e) => handleAddToShoppingList(e, item.name)}
                  title={`"${item.name}" zur Einkaufsliste hinzufügen`}
                  aria-label={`"${item.name}" zur Einkaufsliste hinzufügen`}
                  className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg
                             text-neutral-400 hover:text-emerald-600 dark:hover:text-emerald-400
                             hover:bg-emerald-50 dark:hover:bg-emerald-500/10
                             transition-all duration-200 focus:outline-none focus-visible:ring-2
                             focus-visible:ring-emerald-400"
                >
                  <ShoppingCart className="w-4 h-4" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="flex flex-col items-center justify-center py-10 text-neutral-400 gap-2">
          <RefreshCw className="w-10 h-10 opacity-20" />
          <p className="text-sm">Noch keine Muster erkannt</p>
          <p className="text-xs text-neutral-300 dark:text-neutral-600">
            Nach mehreren Einkäufen erscheinen hier wiederkehrende Artikel
          </p>
        </div>
      )}
    </Card>
  );
};

// =============================================
// Budget Progress Card
// =============================================
const BudgetProgressCard: React.FC<{
  data: BudgetStatusResponse;
  onNavigate: () => void;
}> = ({ data, onNavigate }) => {
  const budgets = data.budgets || [];

  const barColor = (pct: number): string => {
    if (pct >= 90) return 'bg-red-500';
    if (pct >= 70) return 'bg-amber-400';
    return 'bg-emerald-500';
  };

  const textColor = (pct: number): string => {
    if (pct >= 90) return 'text-red-600 dark:text-red-400';
    if (pct >= 70) return 'text-amber-600 dark:text-amber-400';
    return 'text-emerald-600 dark:text-emerald-400';
  };

  return (
    <Card className="animate-slide-up animate-fill-both" padding="lg">
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-violet-50 dark:bg-violet-500/10 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-violet-500" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-base font-bold text-neutral-800 dark:text-white">Budgets</h2>
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">Ausgaben vs. Limit</p>
          </div>
        </div>
        <button
          onClick={onNavigate}
          className="text-xs font-semibold text-primary-500 hover:text-primary-600 px-2 py-1 rounded-lg hover:bg-primary-50 transition-all"
        >
          Verwalten
        </button>
      </div>

      <div className="space-y-3">
        {budgets.map((b) => {
          const pct = Math.min(Math.round(Number(b.percentage) || 0), 100);
          const amount = Number(b.amount) || 0;
          const spent = Number(b.spent) || 0;
          const overBudget = Number(b.percentage) > 100;

          return (
            <div
              key={b.id}
              className={`rounded-xl px-3 py-2.5 transition-all ${
                overBudget
                  ? 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 animate-pulse-border'
                  : 'bg-neutral-50 dark:bg-white/5'
              }`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 truncate">
                  {b.category_name}
                </span>
                <span className={`text-xs font-bold tabular-nums shrink-0 ml-2 ${textColor(Number(b.percentage) || 0)}`}>
                  {pct}%
                </span>
              </div>
              <div className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-700 ${barColor(Number(b.percentage) || 0)}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-neutral-400">
                  {b.period === 'weekly' ? 'Woche' : 'Monat'}
                </span>
                <span className="text-xs font-mono tabular-nums text-neutral-500 dark:text-neutral-400">
                  {formatEUR(spent)} / {formatEUR(amount)}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gesamt-Zeile */}
      {budgets.length > 1 && (
        <div className="mt-4 pt-3 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">Gesamt</span>
          <span className="text-sm font-bold font-mono tabular-nums text-neutral-700 dark:text-neutral-200">
            {formatEUR(Number(data.total_spent) || 0)} / {formatEUR(Number(data.total_budget) || 0)}
          </span>
        </div>
      )}
    </Card>
  );
};

// =============================================
// Partner Activity Feed
// =============================================
interface PartnerActivityItem {
  id: string;
  store: string;
  amount: number;
  date: string;
}

const PartnerActivityCard: React.FC<{
  partnerName: string;
  activities: PartnerActivityItem[];
}> = ({ partnerName, activities }) => {
  const initials = partnerName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const formatRelativeDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.round(
      (now.setHours(0, 0, 0, 0) - date.setHours(0, 0, 0, 0)) / (1000 * 60 * 60 * 24)
    );
    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Gestern';
    if (diffDays <= 7) return `vor ${diffDays} Tagen`;
    return new Date(dateStr).toLocaleDateString('de-DE', { day: '2-digit', month: 'short' });
  };

  return (
    <Card className="animate-slide-up animate-fill-both" padding="lg">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-pink-50 dark:bg-pink-500/10 flex items-center justify-center">
          <Users className="w-4 h-4 text-pink-500" aria-hidden="true" />
        </div>
        <div>
          <h2 className="text-base font-bold text-neutral-800 dark:text-white">Haushalt-Aktivitat</h2>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-0.5">
            Was {partnerName} zuletzt gemacht hat
          </p>
        </div>
      </div>

      {activities.length > 0 ? (
        <div className="space-y-2.5">
          {activities.map((item) => (
            <div key={item.id} className="flex items-start gap-3">
              {/* Avatar */}
              <div
                className="shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-pink-400 to-violet-500
                           flex items-center justify-center text-white text-xs font-bold"
                aria-hidden="true"
              >
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-neutral-700 dark:text-neutral-200 leading-snug">
                  <span className="font-semibold">{partnerName}</span>
                  {' '}hat bei{' '}
                  <span className="font-semibold">{item.store}</span>
                  {' '}eingekauft
                </p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-bold font-mono tabular-nums text-neutral-600 dark:text-neutral-300">
                    {formatEUR(item.amount)}
                  </span>
                  <span className="text-neutral-300 dark:text-neutral-600 text-xs">·</span>
                  <span className="text-xs text-neutral-400">{formatRelativeDate(item.date)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-8 text-neutral-400 gap-2">
          <Users className="w-8 h-8 opacity-30" />
          <p className="text-sm">Noch keine Aktivitat</p>
        </div>
      )}
    </Card>
  );
};

// =============================================
// Dashboard Page
// =============================================
export const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [period, setPeriod] = useState<Period>('all');

  const { data: summary, isLoading: loadingStats, isError: errorStats } = useQuery<SummaryResponse>({
    queryKey: ['dashboard-stats', period],
    queryFn: () => statsApi.getSummary(period),
  });

  const { data: categories, isLoading: loadingCategories, isError: errorCategories } = useQuery<CategorySpendingItem[]>({
    queryKey: ['dashboard-categories', period],
    queryFn: () => {
      const now = new Date();
      if (period === 'week') {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        return statsApi.getByCategory({ date_from: weekAgo.toISOString().slice(0, 10), date_to: now.toISOString().slice(0, 10) });
      }
      if (period === 'month') {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        return statsApi.getByCategory({ date_from: monthAgo.toISOString().slice(0, 10), date_to: now.toISOString().slice(0, 10) });
      }
      return statsApi.getByCategory();
    },
  });

  const { data: savingsByStore, isLoading: loadingSavings, isError: errorSavings } = useQuery<StoreSavingsItem[]>({
    queryKey: ['savings-by-store'],
    queryFn: statsApi.getSavingsByStore,
  });

  const { data: recentData, isLoading: loadingRecent, isError: errorRecent } = useQuery({
    queryKey: ['recent-receipts'],
    queryFn: () => receiptsApi.list({ page: 1, page_size: 8 }),
  });

  const { data: recurringExpenses = [] } = useQuery<RecurringExpense[]>({
    queryKey: ['recurring-expenses'],
    queryFn: recurringExpensesApi.list,
    staleTime: 5 * 60 * 1000,
  });

  const { data: todoSummary } = useQuery<TodoSummary>({
    queryKey: ['todo-summary'],
    queryFn: todosApi.summary,
    staleTime: 2 * 60 * 1000,
  });

  const { data: recurringPurchases = [] } = useQuery<RecurringPurchase[]>({
    queryKey: ['recurring-purchases-stats'],
    queryFn: statsApi.getRecurringPurchases,
    staleTime: 10 * 60 * 1000,
  });

  const { data: budgetStatus } = useQuery<BudgetStatusResponse>({
    queryKey: ['budget-status'],
    queryFn: budgetsApi.getStatus,
    staleTime: 5 * 60 * 1000,
  });

  const { data: households = [] } = useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: householdsApi.list,
    staleTime: 10 * 60 * 1000,
  });

  if (loadingStats || loadingCategories || loadingRecent || loadingSavings) {
    return <LoadingSpinner fullPage text="Dashboard wird geladen..." />;
  }

  if (errorStats || errorCategories || errorSavings || errorRecent) {
    return <Card><p className="text-center py-8 text-error-500">Daten konnten nicht geladen werden. Bitte versuche es erneut.</p></Card>;
  }

  const stats = {
    total_spent: Number(summary?.total_spent) || 0,
    monthly_spent: Number(summary?.current_month_spent) || 0,
    receipt_count: Number(summary?.receipt_count) || 0,
    average_receipt: Number(summary?.average_per_receipt) || 0,
    total_savings: Number(summary?.total_savings) || 0,
  };

  const receipts = recentData?.items || [];
  const storeSavingsData = savingsByStore || [];
  const categoryData = categories || [];

  // Monatsübersicht: Fixkosten monatlich (client-seitig berechnet)
  const activeExpenses = recurringExpenses.filter((e) => e.is_active);
  const fixkostenMonatlich = activeExpenses.reduce(
    (sum, e) => sum + toMonthlyAmountDash(Number(e.amount) || 0, e.interval),
    0,
  );
  const variableAusgaben = Number(summary?.current_month_spent) || 0;

  // Budget-Status: nur anzeigen wenn mindestens 1 Budget existiert
  const hasBudgets = (budgetStatus?.budgets?.length ?? 0) > 0;

  // Partner-Aktivitat: Haushalt-Mitglied das nicht der aktuelle User ist
  const household = households.length > 0 ? households[0] : null;
  const partnerMember = household?.members.find((m) => m.user_id !== user?.id);
  const partnerActivities: PartnerActivityItem[] = partnerMember
    ? receipts
        .filter((r) => r.user_id === partnerMember.user_id)
        .slice(0, 5)
        .map((r) => ({
          id: r.id,
          store: r.store?.name || r.store_name || 'Unbekannter Laden',
          amount: Number(r.total_amount) || 0,
          date: r.receipt_date || r.created_at,
        }))
    : [];

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  const today = new Date().toLocaleDateString('de-DE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const periodSubtitle = period === 'week' ? 'Diese Woche' : period === 'month' ? 'Dieser Monat' : 'Alle Zeiten';

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="animate-fade-in">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold text-neutral-800 dark:text-white">
              {greeting()}, {user?.name || 'du'}! {'\uD83D\uDC4B'}
            </h1>
            <p className="text-neutral-400 dark:text-neutral-500 mt-1.5 text-sm">{today}</p>
          </div>
          <div className="hidden sm:block text-right">
            <div className="text-4xl animate-float">{'\uD83E\uDDFE'}</div>
          </div>
        </div>
      </div>

      {/* Period Filter Tabs */}
      <div className="animate-fade-in">
        <PeriodFilterTabs active={period} onChange={setPeriod} />
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          title="Gesamtausgaben"
          value={formatEUR(stats.total_spent)}
          emoji={'\uD83D\uDCB0'}
          gradient="bg-gradient-to-br from-indigo-500 via-indigo-600 to-violet-600"
          subtitle={periodSubtitle}
          delay={100}
          onClick={() => navigate('/receipts')}
        />
        <StatCard
          title="Dieser Monat"
          value={formatEUR(stats.monthly_spent)}
          emoji={'\uD83D\uDCC5'}
          gradient="bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500"
          trend={
            Number(summary?.month_over_month_change) > 0
              ? 'up'
              : Number(summary?.month_over_month_change) < 0
                ? 'down'
                : undefined
          }
          trendValue={
            Number(summary?.month_over_month_change) !== 0
              ? `${Number(summary?.month_over_month_change) > 0 ? '+' : ''}${Number(summary?.month_over_month_change).toFixed(0)}%`
              : undefined
          }
          subtitle="vs. letzter Monat"
          delay={200}
          onClick={() => navigate('/receipts')}
        />
        <StatCard
          title="Ersparnis"
          value={formatEUR(stats.total_savings)}
          emoji={'\uD83C\uDF89'}
          gradient="bg-gradient-to-br from-amber-400 via-orange-500 to-rose-500"
          subtitle="Rabatte & Gutscheine"
          delay={300}
          onClick={() => navigate('/stats')}
        />
        <StatCard
          title="Durchschnitt"
          value={formatEUR(stats.average_receipt)}
          emoji={'\uD83D\uDCCA'}
          gradient="bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-500"
          subtitle="Pro Einkauf"
          delay={400}
          onClick={() => navigate('/stats')}
        />
      </div>

      {/* Budget Progress (nur wenn Budgets existieren) */}
      {hasBudgets && budgetStatus && (
        <BudgetProgressCard
          data={budgetStatus}
          onNavigate={() => navigate('/settings')}
        />
      )}

      {/* Top Categories + Recent Receipts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Donut: Top Categories */}
        <CategoryDonutCard data={categoryData} />

        {/* Recent Receipts as Timeline */}
        <Card padding="none" className="animate-slide-up animate-fill-both animation-delay-500">
          <div className="p-5 pb-3 flex items-center justify-between">
            <h2 className="text-lg font-bold text-neutral-800 dark:text-white flex items-center gap-2">
              {'\u23F0'} Letzte Einkäufe
            </h2>
            <button
              onClick={() => navigate('/receipts')}
              className="text-sm text-primary-500 hover:text-primary-600 font-semibold
                         hover:bg-primary-50 px-3 py-1 rounded-lg transition-all duration-200"
            >
              Alle
            </button>
          </div>
          <div className="px-2 pb-3">
            {receipts.length > 0 ? (
              receipts.map((receipt, index) => (
                <ReceiptTimelineItem
                  key={receipt.id}
                  receipt={receipt}
                  onClick={() => navigate(`/receipts/${receipt.id}`)}
                  index={index}
                />
              ))
            ) : (
              <div className="py-12 text-center animate-bounce-in">
                <span className="text-5xl block mb-3">{'\uD83D\uDCF8'}</span>
                <p className="text-neutral-500 dark:text-neutral-400 font-medium">Noch keine Belege</p>
                <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-1">Scanne deinen ersten Kassenzettel!</p>
                <button
                  onClick={() => navigate('/receipts/new')}
                  className="mt-4 text-sm font-semibold text-primary-500 hover:text-primary-600
                             bg-primary-50 hover:bg-primary-100 px-4 py-2 rounded-xl transition-all duration-200"
                >
                  Jetzt starten {'\uD83D\uDE80'}
                </button>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Savings by Store + Fixkosten */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <SavingsByStoreCard data={storeSavingsData} />
        <FixkostenCard
          expenses={recurringExpenses}
          onNavigate={() => navigate('/recurring')}
        />
      </div>

      {/* Monatsübersicht Donut */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <MonatsuebersichtCard
          fixkostenMonatlich={fixkostenMonatlich}
          variableAusgaben={variableAusgaben}
        />
      </div>

      {/* Aufgaben Widget */}
      {todoSummary && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <AufgabenCard
            summary={todoSummary}
            onNavigate={() => navigate('/todos')}
          />
        </div>
      )}

      {/* Haushalt-Aktivitat (Partner-Feed) — nur wenn Partner im Haushalt */}
      {partnerMember && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <PartnerActivityCard
            partnerName={partnerMember.user_name}
            activities={partnerActivities}
          />
        </div>
      )}

      {/* Wiederkehrende Einkäufe Widget */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <WiederkehrendeEinkäufeCard items={recurringPurchases} />
      </div>

    </div>
  );
};
